import React, { useState } from "react";
import {
  Star,
  ChevronDown,
  ChevronUp,
  Instagram,
  Check,
  Award,
} from "lucide-react";
import Avatar from "./Avatar";

// ============================================================================
// Master toggles — temporarily disabled while Google Ads review is pending.
// Flip to `true` to bring buttons back across the entire site.
// ============================================================================
const SHOW_WHATSAPP_WIDGET = false;
const SHOW_INSTAGRAM_WIDGET = false;

// Inline WhatsApp glyph (since lucide doesn't have a WhatsApp icon)
const WhatsAppIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
    <path d="M19.11 17.36c-.27-.14-1.6-.79-1.85-.88-.25-.09-.43-.14-.61.14-.18.27-.7.88-.86 1.06-.16.18-.32.2-.59.07-.27-.14-1.14-.42-2.17-1.33-.8-.71-1.34-1.59-1.5-1.86-.16-.27-.02-.42.12-.55.12-.12.27-.32.4-.48.13-.16.18-.27.27-.45.09-.18.04-.34-.02-.48-.07-.14-.61-1.46-.83-2-.22-.53-.45-.46-.61-.47-.16-.01-.34-.01-.52-.01-.18 0-.48.07-.73.34-.25.27-.95.93-.95 2.27 0 1.34.97 2.64 1.11 2.82.14.18 1.92 2.92 4.65 4.1.65.28 1.16.45 1.55.58.65.21 1.24.18 1.71.11.52-.08 1.6-.65 1.82-1.28.22-.62.22-1.16.16-1.28-.07-.12-.25-.18-.52-.32zM16.03 5.5c-5.79 0-10.5 4.71-10.5 10.5 0 1.85.49 3.65 1.41 5.24L5.5 26.5l5.41-1.42c1.54.84 3.27 1.29 5.03 1.29h.01c5.79 0 10.5-4.71 10.5-10.5 0-2.81-1.09-5.45-3.08-7.43-1.99-1.99-4.63-3.08-7.34-3.08zm0 19.05h-.01c-1.57 0-3.11-.42-4.46-1.22l-.32-.19-3.32.87.89-3.24-.21-.33c-.89-1.41-1.36-3.05-1.36-4.73 0-4.9 3.99-8.88 8.89-8.88 2.37 0 4.6.93 6.27 2.61 1.68 1.68 2.6 3.91 2.6 6.28-.01 4.9-3.99 8.83-8.97 8.83z"/>
  </svg>
);

const Stars = ({ rating, size = "w-4 h-4" }) => {
  const filled = Math.round((rating / 10) * 5);
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`${size} ${i < filled ? "text-[#f5b51e]" : "text-slate-300"}`}
          fill="currentColor"
        />
      ))}
    </div>
  );
};

const AstrologerCard = ({ astrologer, index }) => {
  const [expanded, setExpanded] = useState(false);
  const contactEnabled = astrologer.contactEnabled !== false; // default true
  const cleanPhone = (astrologer.whatsapp || "").replace(/[^0-9]/g, "");
  // Outbound link points to our own domain — /go/whatsapp/:id.
  // Currently the route shows a "not available" page (review-pending mode).
  const whatsappLink =
    contactEnabled && cleanPhone ? `/go/whatsapp/${astrologer.id}` : null;
  const whatsappAvailable = SHOW_WHATSAPP_WIDGET && !!cleanPhone;
  // Outbound social links fully removed while Google Ads review is active.
  // Restore the previous instaLink construction once approval is granted.
  const instaLink = null;

  return (
    <div className="card-light overflow-hidden fade-in relative">
      {/* Editor's Choice ribbon for #1 */}
      {index === 0 && (
        <div className="absolute top-3 left-0 z-10">
          <div className="editor-ribbon text-[10px] uppercase px-3 py-1">
            Editor's Choice
          </div>
        </div>
      )}

      {/* ===== MOBILE LAYOUT (stacked) ===== */}
      <div className="sm:hidden p-4 pt-9">
        <div className="flex items-start gap-3">
          <div className="relative shrink-0">
            <Avatar
              astrologer={astrologer}
              sizeClass="w-20 h-20"
              ringClass="ring-2 ring-slate-100"
            />
            <div className="absolute -bottom-1 -left-1 text-[10px] font-extrabold text-white bg-[#0b1e3f] rounded-full w-6 h-6 flex items-center justify-center">
              {String(index + 1).padStart(2, "0")}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-[#0b1e3f] leading-tight">{astrologer.name}</h3>
            <p className="text-xs text-[#16a3a8] font-semibold mt-0.5">{astrologer.title}</p>
            <div className="flex items-center gap-1.5 mt-1">
              <Stars rating={astrologer.rating} size="w-3.5 h-3.5" />
              <span className="text-[11px] text-slate-500">
                {astrologer.experience}
              </span>
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-2xl font-extrabold text-[#0b1e3f] leading-none">
              {astrologer.rating.toFixed(1)}
            </div>
            <div className="text-[10px] text-slate-500 uppercase font-semibold mt-0.5">Rating</div>
          </div>
        </div>

        <ul className="mt-3 space-y-1.5">
          {astrologer.bullets.slice(0, 3).map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-[13px] text-slate-700 leading-snug">
              <Check className="w-3.5 h-3.5 text-[#16a3a8] mt-0.5 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <div className="mt-3 flex flex-wrap gap-1">
          {astrologer.expertise.slice(0, 3).map((e) => (
            <span
              key={e}
              className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200"
            >
              {e}
            </span>
          ))}
        </div>

        {/* WhatsApp widget mobile */}
        <div className="mt-4 space-y-2">
          {whatsappAvailable && (
            <WhatsAppWidget
              link={whatsappLink}
              phone={astrologer.whatsapp}
              disabled={!contactEnabled}
              astrologerName={astrologer.name}
              astrologerId={astrologer.id}
              compact
            />
          )}
          {instaLink && (
            <a
              href={instaLink}
              target="_blank"
              rel="noreferrer"
              className="btn-instagram w-full py-2 rounded-md text-xs uppercase flex items-center justify-center gap-2"
            >
              <Instagram className="w-3.5 h-3.5" /> @{astrologer.instagram}
            </a>
          )}
        </div>
      </div>

      {/* ===== DESKTOP LAYOUT ===== */}
      <div className="hidden sm:grid grid-cols-12 gap-0">
        <div className="col-span-1 flex flex-col items-center justify-center pt-10 pb-6">
          <div className="text-3xl sm:text-4xl font-extrabold text-slate-400">
            {String(index + 1).padStart(2, "0")}
          </div>
          {astrologer.badge && (
            <div className="mt-2 text-[10px] uppercase tracking-wider text-[#16a3a8] font-bold flex items-center gap-1">
              <Award className="w-3 h-3" />
            </div>
          )}
        </div>

        <div className="col-span-3 lg:col-span-2 flex items-center justify-center p-4">
          <Avatar
            astrologer={astrologer}
            sizeClass="w-24 h-24 lg:w-28 lg:h-28"
          />
        </div>

        <div className="col-span-4 lg:col-span-5 p-5">
          <h3 className="text-lg lg:text-xl font-bold text-[#0b1e3f]">{astrologer.name}</h3>
          <p className="text-sm text-[#16a3a8] font-semibold">{astrologer.title}</p>
          <div className="flex items-center gap-2 mt-1">
            <Stars rating={astrologer.rating} />
            <span className="text-xs text-slate-500">
              {astrologer.experience} • {astrologer.consultations}
            </span>
          </div>

          <ul className="mt-3 space-y-1.5">
            {astrologer.bullets.slice(0, 4).map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <Check className="w-4 h-4 text-[#16a3a8] mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {astrologer.expertise.slice(0, 4).map((e) => (
              <span
                key={e}
                className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200"
              >
                {e}
              </span>
            ))}
          </div>
        </div>

        <div className="col-span-4 p-4 flex flex-col items-center justify-center border-l border-slate-100">
          <Stars rating={astrologer.rating} />
          <div className="text-3xl font-extrabold text-[#0b1e3f] mt-1 leading-none">
            {astrologer.rating.toFixed(1)}
          </div>
          <div className="text-[10px] text-slate-500 uppercase font-semibold mt-0.5">Rating</div>
          <div className="mt-3 w-full space-y-2">
            {whatsappAvailable && (
              <WhatsAppWidget
                link={whatsappLink}
                phone={astrologer.whatsapp}
                disabled={!contactEnabled}
                astrologerName={astrologer.name}
                astrologerId={astrologer.id}
                compact
              />
            )}
            {instaLink && (
              <a
                href={instaLink}
                target="_blank"
                rel="noreferrer"
                className="btn-instagram w-full py-2 rounded-md text-[11px] uppercase flex items-center justify-center gap-1.5 truncate"
              >
                <Instagram className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">@{astrologer.instagram}</span>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full border-t border-slate-100 py-2 text-sm text-slate-600 font-semibold flex items-center justify-center gap-1 hover:bg-slate-50"
      >
        {expanded ? <>Show Less <ChevronUp className="w-4 h-4" /></> : <>Show More <ChevronDown className="w-4 h-4" /></>}
      </button>

      {expanded && (
        <div className="p-4 sm:p-6 border-t border-slate-100 fade-in bg-slate-50/60">
          <div className="text-center">
            <div className="text-[11px] uppercase tracking-widest text-[#16a3a8] font-bold">
              {astrologer.tagline}
            </div>
            <h4 className="text-lg sm:text-xl font-bold text-[#0b1e3f] mt-1">{astrologer.name}</h4>
          </div>

          <div className="grid md:grid-cols-2 gap-5 sm:gap-6 mt-5 sm:mt-6">
            <div>
              <h5 className="text-sm font-bold text-[#0b1e3f] mb-2">Who It's Best for:</h5>
              <ul className="space-y-1.5">
                {astrologer.bestFor.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-[#16a3a8] mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h5 className="text-sm font-bold text-[#0b1e3f] mb-2">Highlights:</h5>
              <ul className="space-y-1.5">
                {astrologer.highlights.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-[#16a3a8] mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-5 sm:mt-6 overflow-hidden rounded border border-slate-200 text-sm">
            <table className="w-full">
              <tbody>
                <tr className="border-b border-slate-200">
                  <td className="font-bold text-[#0b1e3f] p-3 bg-white w-1/2">Price Range:</td>
                  <td className="p-3 bg-white text-slate-700">{astrologer.priceRange}</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="font-bold text-[#0b1e3f] p-3 bg-slate-50">Session Modes:</td>
                  <td className="p-3 bg-slate-50 text-slate-700">{astrologer.sessionModes}</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="font-bold text-[#0b1e3f] p-3 bg-white">Languages:</td>
                  <td className="p-3 bg-white text-slate-700">{astrologer.languages.join(", ")}</td>
                </tr>
                <tr>
                  <td className="font-bold text-[#0b1e3f] p-3 bg-slate-50">Expertise:</td>
                  <td className="p-3 bg-slate-50 text-slate-700">{astrologer.expertise.join(", ")}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mt-5 sm:mt-6 text-sm text-slate-700 leading-relaxed">
            {astrologer.description}
          </p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            {whatsappAvailable && (
              contactEnabled ? (
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    if (typeof window !== "undefined" && typeof window.gtag === "function") {
                      window.gtag("event", "whatsapp_click", {
                        astrologer_name: astrologer.name,
                        astrologer_id: astrologer.id,
                        location: "expanded_cta",
                      });
                    }
                  }}
                  className="btn-whatsapp px-6 py-3 rounded text-sm inline-flex items-center justify-center gap-2 uppercase"
                >
                  <WhatsAppIcon /> Chat on WhatsApp
                </a>
              ) : (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  aria-disabled="true"
                  className="btn-whatsapp px-6 py-3 rounded text-sm inline-flex items-center justify-center gap-2 uppercase"
                >
                  <WhatsAppIcon /> Chat on WhatsApp
                </button>
              )
            )}
            {instaLink && (
              <a href={instaLink} target="_blank" rel="noreferrer" className="btn-instagram px-5 py-3 rounded text-sm inline-flex items-center justify-center gap-2 uppercase">
                <Instagram className="w-4 h-4" /> Follow @{astrologer.instagram}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============== Reusable WhatsApp widget ==============
const maskPhone = (phone) => {
  if (!phone) return "";
  const clean = phone.replace(/\s+/g, "");
  if (clean.length <= 7) return clean;
  const visible = clean.slice(0, clean.length - 5);
  return `${visible}*****`;
};

const WhatsAppWidget = ({ link, phone, compact = false, disabled = false, astrologerName = "", astrologerId = "" }) => {
  const handleActiveClick = () => {
    // Fire Google Analytics conversion event
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "whatsapp_click", {
        astrologer_name: astrologerName,
        astrologer_id: astrologerId,
        masked_phone: maskPhone(phone),
        location: compact ? "card_widget" : "card_widget_full",
      });
    }
    // Do not prevent default — the link continues to open WhatsApp.
  };

  // Inner card content (identical look in both states)
  const Inner = (
    <div className={`relative overflow-hidden rounded-lg border border-[#25d366]/30 bg-gradient-to-br from-emerald-50 to-white ${compact ? "p-2" : "p-3"}`}>
      <div className="flex items-center gap-2">
        <div className="relative shrink-0">
          <div className={`${compact ? "w-8 h-8" : "w-9 h-9"} rounded-full bg-[#25d366] flex items-center justify-center text-white wa-pulse`}>
            <WhatsAppIcon className={compact ? "w-4 h-4" : "w-5 h-5"} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold leading-none">
            WhatsApp Now
          </div>
          <div className={`${compact ? "text-[12px]" : "text-[13px]"} font-bold text-[#0b1e3f] mt-0.5 leading-tight tracking-wider whitespace-nowrap`}>
            {maskPhone(phone)}
          </div>
        </div>
      </div>
      <div className="mt-2 btn-whatsapp w-full text-center py-1.5 rounded text-[11px] uppercase flex items-center justify-center gap-1.5">
        <WhatsAppIcon className="w-3.5 h-3.5" /> Chat Now
      </div>
    </div>
  );

  // Disabled — looks identical but click is a no-op (no navigation, no GA event)
  if (disabled) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        aria-disabled="true"
        className="block w-full text-left"
      >
        {Inner}
      </button>
    );
  }

  // Active — real WhatsApp link, also fires GA whatsapp_click event
  return (
    <a
      href={link}
      target="_blank"
      rel="noreferrer"
      onClick={handleActiveClick}
      className="block group"
    >
      {Inner}
    </a>
  );
};

export default AstrologerCard;
